/* eslint-disable @typescript-eslint/no-unused-vars */
import { motion } from 'framer-motion';
import { groupBy } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';

import { ItemAssistant } from './ItemsAssistant';

import { ArrowDateSelector } from '@/components/GenericsTournaments';
import useAuth from '@/hooks/useAuth';
import useToast from '@/hooks/useToast';
import useTourney from '@/hooks/useTourney';
import { DateGDT, GDTApiErrors } from '@/types';
import { componentsLastFixture } from '@/types/fieldAssistance';
import 'moment/dist/locale/es';
import { VariantFieldAssistance } from '@/types/team';
import { FADE_IN_OUT, cn } from '@/utils';
// eslint-disable-next-line import/order
import { getFixtureRequest } from '@/services/fieldAssistant';

interface Props extends VariantFieldAssistance {
  isInterzonal: boolean;
  hideZones: boolean;
}

export const FixtureTable = ({ variant, isInterzonal, hideZones }: Props) => {
  const { userInitialData } = useAuth();

  const { tourneyData, updateTourney } = useTourney();
  const toast = useToast();

  const matchDayOrder = useMemo(
    () => tourneyData.matchDay?.orden ?? 0,
    [tourneyData.matchDay?.orden]
  );

  const {
    data: fixture,
    isFetching: fetchingFixture,
    isLoading: loadingFixture,
  } = useQuery(
    ['fixtureMatchDay', matchDayOrder],
    async () => {
      const response = await getFixtureRequest(matchDayOrder);

      if (!response.estado) {
        throw new Error(response.mensaje);
      }

      return response;
    },
    {
      keepPreviousData: true,
      enabled: matchDayOrder > 0,
      refetchOnWindowFocus: false,
      onError: (error: GDTApiErrors) => {
        toast.show(error.mensaje);
      },
    }
  );

  const fixtureData = useMemo((): DateGDT[] => {
    if (!userInitialData || !userInitialData?.fechaActiva) return [];
    return userInitialData?.todasLasFechas.filter(
      (f) => f.orden <= userInitialData?.fechaActiva.orden
    );
  }, [userInitialData]);

  useEffect(() => {
    if (!tourneyData.matchDay && userInitialData?.fechaActiva) {
      updateTourney({ matchDay: userInitialData.fechaActiva });
    }
  }, [tourneyData.matchDay, updateTourney, userInitialData?.fechaActiva]);

  const matchsGroupByDate = useMemo(() => {
    const matchs = fixture?.estado && fixture.fixtureAnteriorTo?.partidos;
    return groupBy(matchs || [], ({ data }) => data?.fechaParaOrdenar);
  }, [fixture]);

  const printFixture = useMemo(() => {
    return Object.values(matchsGroupByDate).map((_ele, index) => {
      const date =
        Object.keys(matchsGroupByDate)[index] !== 'undefined'
          ? Object.keys(matchsGroupByDate)[index]
          : undefined;

      const matchDay = date ? moment(date).format('dddd D [de] MMMM') : '';
      return (
        <div key={index}>
          <p className="bg-gdt-status-green text-center text-white font-medium uppercase text-xs lg:text-sm h-9 flex justify-center items-center">
            {matchDay}
          </p>
          {Object.values(matchsGroupByDate)[index]?.map(
            (
              match: componentsLastFixture['schemas']['PartidoFixtureTO'],
              index
            ) => {
              const data = match?.data;
              return (
                data && (
                  <ItemAssistant.Fixture
                    key={index}
                    dateMatch={data?.horaFormateada?.replaceAll('hs', '')}
                    golsLocal={match.ptosEquipoLocal}
                    golsVisitor={match.ptosEquipoVisitante}
                    hideZones={hideZones}
                    idEstado={match.idEstado}
                    idLocal={data.idLocal || 0}
                    idVisitor={data.idVisitante || 0}
                    isInterzonal={isInterzonal}
                    localClub={data.equipoLocal || ''}
                    visitorClub={data.equipoVisitante || ''}
                    zona={data?.zona}
                  />
                )
              );
            }
          )}
        </div>
      );
    });
  }, [hideZones, isInterzonal, matchsGroupByDate]);

  return (
    <motion.div animate={'show'} initial={'hide'} variants={FADE_IN_OUT}>
      <div
        className={cn('mt-6 mb-5 lg:mb-0 lg:mt-6 min-h-[1000px]', {
          'overflow-auto h-[calc(100vh-170px)]': variant === 'EMBED',
        })}
      >
        {/* selector de fechas */}
        <ArrowDateSelector
          disabled={fetchingFixture || loadingFixture}
          matchsDaysAvailables={fixtureData || []}
        />
        {printFixture}
      </div>
    </motion.div>
  );
};
